import React from 'react'
import { SharedContext } from 'context/shared-context'

function useSafeDispatch(dispatch) {
  const mounted = React.useRef(false)

  React.useLayoutEffect(() => {
    mounted.current = true

    return () => {
      mounted.current = false
    }
  }, [])

  return React.useCallback((...args) => (mounted.current ? dispatch(...args) : void 0), [dispatch])
}

const initialState = { status: 'idle', data: null, error: null }

function useAsync() {
  const [{ status, data, error }, setState] = React.useReducer((s, a) => ({ ...s, ...a }), initialState)
  const { dispatch } = React.useContext(SharedContext)
  const safeSetState = useSafeDispatch(setState)
  const run = React.useCallback(
    (promise, showProgress = false) => {
      if (!promise || !promise.then) {
        throw new Error(
          `The argument passed to useAsync().run must be a promise. Maybe a function that's passed isn't returning anything?`,
        )
      }

      if (showProgress && dispatch) {
        dispatch({ type: 'progress', value: true })
      }

      safeSetState({ status: 'pending' })

      return promise.then(
        (newData) => {
          if (showProgress && dispatch) {
            dispatch({ type: 'progress', value: false })
          }

          safeSetState({ data: newData, status: 'resolved' })

          return newData
        },
        (newError) => {
          if (showProgress && dispatch) {
            dispatch({ type: 'progress', value: false })
          }

          safeSetState({ status: 'rejected', error: newError })

          return newError
        },
      )
    },
    [dispatch, safeSetState],
  )
  const setData = React.useCallback((newData) => safeSetState({ data: newData }), [safeSetState])
  const setError = React.useCallback((newError) => safeSetState({ error: newError }), [safeSetState])
  const reset = React.useCallback(() => safeSetState(initialState), [safeSetState])

  return {
    // using the same names that react-query uses for convenience
    isIdle: status === 'idle',
    isLoading: status === 'pending',
    isError: status === 'rejected',
    isSuccess: status === 'resolved',
    setData,
    setError,
    error,
    status,
    data,
    run,
    reset,
  }
}

export default useAsync
